import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Loader2, RefreshCw, Search, ScrollText, CheckCircle2, XCircle, MessageCircle } from "lucide-react";

const fmtAuditTs = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "adesso";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `oggi ${time}`;
  return `${d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" })} ${time}`;
};
const fmtAbs = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })} ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
};

const CAT_COLORS = {
  Autenticazione: "bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300",
  Amministratori: "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
  Pagamenti: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  "Condomìni": "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300",
  Pratiche: "bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300",
  Documenti: "bg-teal-100 text-teal-700 dark:bg-teal-950/50 dark:text-teal-300",
  Comunicazioni: "bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300",
  Portali: "bg-pink-100 text-pink-700 dark:bg-pink-950/50 dark:text-pink-300",
};
const catColor = (c) => CAT_COLORS[c] || "bg-secondary text-muted-foreground";

const KIND_LABEL = { credenziali: "Credenziali portale", pratica: "Pratica", avviso: "Avviso", sondaggio: "Sondaggio", generico: "SMS" };
const SMS_STATUS = {
  inviato: { label: "Inviato", ok: true, cls: "bg-secondary text-muted-foreground" },
  consegnato: { label: "Consegnato", ok: true, cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" },
  errore: { label: "Errore", ok: false, cls: "bg-red-100 text-red-700" },
  non_consegnato: { label: "Non consegnato", ok: false, cls: "bg-red-100 text-red-700" },
  scaduto: { label: "Scaduto", ok: false, cls: "bg-amber-100 text-amber-700" },
};

export default function RegistroAttivita() {
  const { user } = useAuth();
  const [view, setView] = useState("attivita");
  const [events, setEvents] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [smsEvents, setSmsEvents] = useState([]);
  const [smsLoading, setSmsLoading] = useState(false);

  const load = useCallback((c = "all", query = "") => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "300" });
    if (c && c !== "all") params.set("category", c);
    if (query) params.set("q", query);
    api.get(`/admin/audit-log?${params.toString()}`)
      .then((r) => { setEvents(r.data.events || []); setCats(r.data.categories || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadSms = useCallback((query = "") => {
    setSmsLoading(true);
    const params = new URLSearchParams({ limit: "300" });
    if (query) params.set("q", query);
    api.get(`/admin/sms-log?${params.toString()}`)
      .then((r) => setSmsEvents(r.data.events || []))
      .catch(() => {})
      .finally(() => setSmsLoading(false));
  }, []);

  useEffect(() => { load("all", ""); loadSms(""); }, [load, loadSms]);

  if (user && user.role !== "super_admin") return <Navigate to="/" replace />;

  return (
    <div className="fade-in" data-testid="registro-attivita-page">
      <header className="px-4 sm:px-10 pt-6 sm:pt-10 pb-6 border-b border-border bg-card/50">
        <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-2">Super Admin</div>
        <h1 className="font-head text-4xl font-extrabold tracking-tighter flex items-center gap-3">
          <ScrollText className="h-8 w-8 text-amber-500" />Registro attività
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Cronologia globale di ogni azione svolta sulla piattaforma da tutti gli amministratori.</p>
      </header>

      <div className="p-4 sm:p-10 space-y-4">
        {/* Selettore vista: Attività / SMS */}
        <div className="flex items-center gap-1 bg-secondary rounded-md p-0.5 w-fit" data-testid="registro-view-toggle">
          {[["attivita", "Attività", ScrollText], ["sms", "Storico SMS", MessageCircle]].map(([k, lbl, Icon]) => (
            <button key={k} type="button" data-testid={`registro-view-${k}`} onClick={() => setView(k)}
              className={`text-[12px] font-medium px-3 py-1.5 rounded inline-flex items-center gap-1.5 ${view === k ? "bg-[#007BFF] text-white" : "text-muted-foreground hover:text-foreground"}`}>
              <Icon className="h-3.5 w-3.5" />{lbl}
            </button>
          ))}
        </div>

        {view === "attivita" ? (<>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") load(cat, q); }}
              placeholder="Cerca per email, azione o percorso…" className="pl-9 h-10" data-testid="audit-search" />
          </div>
          <button onClick={() => load(cat, q)} data-testid="audit-refresh"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-md border border-border text-sm font-medium hover:bg-secondary transition-colors">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />Aggiorna
          </button>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap" data-testid="audit-cat-filters">
          {["all", ...cats].map((c) => (
            <button key={c} onClick={() => { setCat(c); load(c, q); }} data-testid={`audit-cat-${c}`}
              className={`text-[12px] px-3 py-1.5 rounded-full border transition-colors ${cat === c ? "bg-[#007BFF] text-white border-[#007BFF]" : "border-border text-muted-foreground hover:bg-secondary"}`}>
              {c === "all" ? "Tutte" : c}
            </button>
          ))}
        </div>

        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="audit-log-list">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : events.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-16">Nessuna attività registrata.</div>
          ) : (
            <div className="divide-y divide-border/60 max-h-[560px] overflow-y-auto scroll-thin" data-testid="audit-log-scroll">
              {events.map((e) => (
                <div key={e.id} className="flex items-start gap-3 px-4 py-3 text-sm hover:bg-secondary/40 list-enter" data-testid={`audit-row-${e.id}`}>
                  <div className="mt-0.5">
                    {e.success ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-red-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{e.action}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${catColor(e.category)}`}>{e.category}</span>
                      {!e.success && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">HTTP {e.status}</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {e.actor_email}{e.actor_role === "super_admin" ? " · Super Admin" : ""} · {e.method} {e.path}
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground whitespace-nowrap">{fmtAuditTs(e.ts)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        </>) : (<>
        {/* Storico invii SMS */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm text-muted-foreground flex-1">Elenco degli SMS inviati (credenziali portali, pratiche, avvisi e sondaggi) con data, destinatario ed esito.</div>
          <button onClick={() => loadSms(q)} data-testid="sms-log-refresh"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-md border border-border text-sm font-medium hover:bg-secondary transition-colors">
            <RefreshCw className={`h-4 w-4 ${smsLoading ? "animate-spin" : ""}`} />Aggiorna
          </button>
        </div>
        <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="sms-log-list">
          {smsLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : smsEvents.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-16">Nessun SMS inviato finora.</div>
          ) : (
            <div className="divide-y divide-border/60 max-h-[560px] overflow-y-auto scroll-thin" data-testid="sms-log-scroll">
              {smsEvents.map((e) => {
                const st = SMS_STATUS[e.status] || SMS_STATUS.inviato;
                return (
                <div key={e.id} className="flex items-start gap-3 px-4 py-3 text-sm hover:bg-secondary/40 list-enter" data-testid={`sms-row-${e.id}`}>
                  <div className="mt-0.5">
                    {st.ok ? <CheckCircle2 className={`h-4 w-4 ${e.status === "consegnato" ? "text-emerald-500" : "text-sky-500"}`} /> : <XCircle className="h-4 w-4 text-red-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium font-mono">{e.recipient}</span>
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">{KIND_LABEL[e.kind] || e.kind}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${st.cls}`}>{st.label}{e.status === "inviato" && e.segments ? ` · ${e.segments} SMS` : ""}</span>
                      {e.delivered_at && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" data-testid={`sms-delivered-${e.id}`}>Consegnato il {fmtAbs(e.delivered_at)}</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {e.building_name || "—"}{e.actor_email ? ` · ${e.actor_email}` : ""}{e.error ? ` · ${e.error}` : ""}
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground whitespace-nowrap">{fmtAuditTs(e.ts)}</div>
                </div>
                );
              })}
            </div>
          )}
        </div>
        </>)}
      </div>
    </div>
  );
}
